// Vendor imports
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CapsuleButton, CircleButton } = require('client-v2/common/components/buttons')
const LazyImage = require('client-v2/common/components/lazy-image')
const Modal = require('client-v2/common/components/modal')
const Icon = require('client-v2/common/containers/icons/component')
const useInfiniteScroll = require('client-v2/common/hooks/infinite-scroll')
const usePrevious = require('client-v2/common/hooks/previous')
const tag = require('client-v2/common/utils/hyperscript')

const bem = BemEntity('CollectionEditCoverImageModal')

function CollectionEditCoverImageModal(props) {
  return (
    tag(Modal, {
      ariaLabel: 'Edit collection cover image',
      onDismiss: props.onDismiss
    },
      tag('div', {
        className: bem.element('content')
      },
        tag('div', {
          className: bem.element('header')
        },
          'Choose collection cover\u00a0image'
        ),

        tag(CollectionEditCoverImageModalList, props),

        tag('div', {
          className: bem.element('modal-buttons')
        },
          tag(CollectionEditCoverImageModalDeleteButton, props),
          tag(CapsuleButton, {
            className: bem.element('cancel-button'),
            onClick: props.onDismiss
          },
            'Dismiss'
          )
        )
      )
    )
  )
}

function CollectionEditCoverImageModalList({
  // Flags
  isPending,
  isNextPageAvailable,
  isNextPagePending,
  // Values
  selectedImage,
  images,
  // Callbacks
  onImagesFetch,
  onSelect
}) {
  // Hooks
  const scrollableRef = React.useRef()
  useInfiniteScroll({
    disabled: isNextPagePending || !isNextPageAvailable,
    scrollableRef,
    onLoadMore: onImagesFetch
  })
  // Render
  return (
    tag('div', {
      ref: scrollableRef,
      className: bem.element('list')
    },
      images.map(image => (
        tag(CollectionEditCoverImageModalItem, {
          key: image,
          isChecked: image === selectedImage,
          isPending,
          value: image,
          onSelect
        })
      )),
      (isNextPageAvailable || isNextPagePending) && (
        tag('div', {
          className: bem.element('loading-indicator', {
            pending: isNextPagePending
          })
        })
      )
    )
  )
}

function CollectionEditCoverImageModalItem({
  // Flags
  isChecked,
  isPending,
  // Values
  value,
  // Callbacks
  onSelect
}) {
  // Hooks
  const previousValue = usePrevious(value)
  const [ isFailed, setFailed ] = React.useState(false)
  const onClick = React.useCallback(
    () => {
      onSelect(value)
    },
    [ onSelect, value ]
  )
  const onError = React.useCallback(
    () => {
      setFailed(true)
    },
    [ setFailed ]
  )
  React.useEffect(
    () => {
      // Reset failed state when value changes
      if (value !== previousValue) {
        setFailed(false)
      }
    },
    [ value, previousValue, setFailed ]
  )
  // Render
  if (isFailed) {
    return null
  }
  return (
    tag('div', {
      className: bem.element('item'),
      onClick
    },
      tag(LazyImage, {
        className: bem.element('image'),
        sources: [ value ],
        onError
      }),
      tag(CircleButton, {
        className: bem.element('select-button', {
          checked: isChecked,
          pending: isPending
        })
      },
        tag(Icon, { id: 'check' })
      )
    )
  )
}

function CollectionEditCoverImageModalDeleteButton({
  isPending,
  selectedImage,
  onDelete
}) {
  if (!selectedImage) {
    return null
  }
  return (
    tag(CapsuleButton, {
      className: bem.element('delete-button'),
      disabled: isPending,
      onClick: onDelete
    },
      'Delete cover image'
    )
  )
}

module.exports = CollectionEditCoverImageModal
